<template>
  <div class="sm-regist">
    <registration-code
      v-if="view == 'code'"
      :codeError="codeError"
      @submit="handleCodeSubmit"
    />

    <div 
      v-else-if="view == 'form'"
      class="sm-regist-inner sm-wrap sm"
      :class="{
        'second-tab': activeTab == 1
      }"
    >
      <div 
        class="sm-regist__left"
        :class="{
          'aligned': pmb.show && providerAddress.line_1
        }"
      >
        <h2>Just a few things to set up your account...</h2>
        <div class="sm-regist__left-tabs">
          <button 
            type="button"
            :class="{
              'active': activeTab == 0,
              'success': activeTab > 0,
            }"
            @click="setActiveTab(0)"
          >
            <include-svg
              v-if="activeTab > 0"
              :name="'icon-reg-success'"
            />
            General
          </button>
          <div class="sm-regist__left-tabs-delimiter">
            <include-svg :name="'icon-reg-delimiter'" />
          </div>
          <button 
            type="button"
            :class="{
              'active': activeTab == 1,
              'success': activeTab > 1
            }"
            @click="setActiveTab(1)"
          >
          <include-svg
              v-if="activeTab > 1"
              :name="'icon-reg-success'"
            />
          
          Address</button>
          <div class="sm-regist__left-tabs-delimiter">
            <include-svg :name="'icon-reg-delimiter'" />
          </div>
          <button 
            type="button"
            :class="{
              'active': activeTab == 2
            }"
            @click="setActiveTab(2)"
          >Verification</button>
        </div>
        <div 
          v-if="providerAddress.line_1 || pmb.show || selectedPlan"
          class="sm-regist__left-info"
        >
          <div 
            v-if="selectedPlan"
            class="sm-regist__left-info-item"
          >
            <p class="sm-txt-strong">Plan</p>
            <div>
              <span>{{selectedPlan.title}}</span>
              <span class="disabled">{{selectedPlanPrice}}</span>
            </div>
          </div>
          <div
            v-if="pmb.show && (!selectedPlan || selectedPlan.has_private_mailbox !== false)"
            class="sm-regist__left-info-item"
          >
            <p class="sm-txt-strong">Unit #</p>
            <div>
              <template v-if="!pmb.active">
                <a
                  href="#"
                  class="sm-link-blue"
                  @click.prevent="pmb.active = true"
                >Customize your mailbox (Unit)</a>
              </template>
              <template v-else>
                <dropdown-block
                  v-model="pmb.number"
                  class="sm-dropdown scroll-y"
                  :options="pmb.list"
                  :searchable="false"
                >
                  <template slot="placeholder">
                    <span>Select unit...</span>
                    <include-svg :name="'icon-arrow-b'" />
                  </template>
                  <template slot="singleLabel" slot-scope="props">
                    <span>{{props.option.id}}</span>
                    <include-svg :name="'icon-arrow-b'" />
                  </template>
                  <template slot="option" slot-scope="props" :value="props.option.id">
                    <span>{{props.option.id}}</span>
                  </template>
                </dropdown-block>
                <a
                  href="#"
                  class="sm-regist__left-info-item-close"
                  @click.prevent="pmb.active = false"
                ></a>
                
              </template>
              <div class="disabled">A new mailbox number will be automatically generated if left unchecked</div>
            </div>
          </div>
          <div
            v-if="providerAddress.line_1"
            class="sm-regist__left-info-item"
          >
            <p class="sm-txt-strong">Virtual Address</p>
            <div>
              <p>
                <span>{{providerAddress.line_1}}, </span>
                <span class="sm-txt-nowrap" v-if="pmb.show && pmb.active && pmb.number">Unit #{{pmb.number.id}}</span>
              </p>
              <p v-if="providerAddress.line_2">{{providerAddress.line_2}}</p>
              <p v-if="providerAddress.line_3">{{providerAddress.line_3}}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="sm-regist__right">
        <form-regist
          :activeTab="activeTab"
          :backErrors="backErrors"
          :formPrefillData="formPrefillData"
          :showOfferings="showOfferings"
          @tabUpdate="activeTab = $event"
          @handleGenerateVerificationCode="handleGenerateVerificationCode"
          @submit="handleFormSubmit"
        />
      </div>
    </div>
    
    <sm-spinner :show="showSpinner" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FormRegist from '@/components/forms/FormRegist';
import Spinner from '@/components/Spinner';
import Multiselect from 'vue-multiselect';
import {GetPlan} from '@/api/plans';
import {FormatPrice, ToUpper} from '@/utils/general-data';
import PeriodSettings from '@/settings/periods';
import ProviderAddress from '@/mixins/ProviderAddress';
import RegistrationCode from '@/views/page-elements/RegistrationCode';

export default {
  inject: ['$validator'],
  name: 'sign-up',
  components: {
    'form-regist': FormRegist,
    'registration-code': RegistrationCode,
    'dropdown-block': Multiselect,
    'sm-spinner': Spinner
  },
  data() {
    return {
      view: 'form', // values: form, code
      activeTab: 0,
      showSpinner: false,
      selectedPlan: null,
      selectedPlanPrice: null,
      pmb: {
        show: false,
        list: [],
        price: 0,
        active: false,
        number: null
      },
      backErrors: [],

      formPrefillData: {},
      codeError: null,
      activationCode: '',
      showOfferings: false
    }
  },
  created() {
    if (this.provider.client_level == 1)
      this.view = 'code';

    if (!this.isDev) {
      // Google Analytics: Event snippet for Sign up - no Subscription conversion page
      gtag('event', 'conversion', {
        'send_to': 'AW-823673197/BqxkCJqIuXoQ7YLhiAM',
        'value': 2
      });

      ga('send', 'event', 'Sign up', 'Enter', this.provider.name, 2);
    }

    if (this.$route.query.first_sso === 'true') {
      this.getUserData().then(res => {
        const acc = res.account;
        this.formPrefillData = {
          email: acc.email,
          first_name: acc.first_name,
          last_name: acc.last_name,
          user_name: acc.first_name + acc.last_name || null,
        }
      })
    }

    const {plan_id , term} = this.$route.query;
    
    if(plan_id && term) {
          GetPlan(plan_id)
              .then(res => {
                if(res) this.showOfferings = true;
                this.selectedPlan = res.plan;
                this.selectedPlanPrice = this.getPlanPrice();
              });
    }

    if (this.provider.allow_user_select_mailbox_number && this.provider.payment_gateway) {
      this.showSpinner = true;
      this.getPMBList()
        .then(res => {
          if (res.length) {
            this.pmb.list = res;
            this.pmb.price = this.provider.price_for_custom_mailbox;
            this.pmb.show = true;
          }
          this.showSpinner = false;
        })
        .catch(e => {
          console.log(e);
          this.showSpinner = false;
        });
    }
  },
  computed: {
    ...mapGetters({
      isDev: 'isDev',
      provider: 'provider',
      user: 'auth/user',
    })
  },
  mixins: [ProviderAddress],
  methods: {
    ...mapActions({
      generateVerificationCode: 'auth/generateVerificationCode',
      createUser: 'auth/createUser',
      activateUserLvl1: 'auth/activateUserLvl1',
      showGlobalNotification: 'showGlobalNotification',
      subscriptionToPlan: 'account/subscriptionToPlan',
      getPMBList: 'billing/getPMBList',
      validateActivationCode: 'auth/validateActivationCode',
      getUserData: 'account/userData',
    }),
    toUpper: ToUpper,
    setActiveTab(index) {
      this.activeTab = index;
      this.errors.clear();
    },
    getPlanPrice() {
      if (this.$route.query.term == 'monthly') {
        return `${FormatPrice(this.selectedPlan.subscription_price)}/month`;
      } else {
        return `${FormatPrice(this.selectedPlan.period_amount)}/${PeriodSettings.alts[this.selectedPlan.period_unit]}`;
      }
    },
    handleFormSubmit(data) {
      this.showSpinner = true;

      if (this.provider.client_level != 1) {
        this.createUser({
          account: data
        })
          .then(() => {
            if (this.provider.payment_gateway && this.$route.query.plan_id) {
              this.subscriptionToPlan({
                plan_id: this.$route.query.plan_id,
                term: this.$route.query.term || 'monthly'
              })
                .then(() => {;
                  this.$router.push(`/billing-info${this.addQuery()}`);
                });
            } else if (!this.provider.payment_gateway){
                this.$router.push(`/billing-info${this.addQuery()}`);
            } else {
              this.$router.push(`/select-plan${this.addQuery()}`);
            }
            //this.showSpinner = false;
          })
          .catch(e => {
            this.showSpinner = false;
            console.log('user not created', e.response.data.errors);

            if (e.response && e.response.data && e.response.data.errors) {
              let err = e.response.data.errors;
              let msg = '';

              if (err.base) {
                msg = e.response.data.errors.base;

                this.showGlobalNotification({
                  type: 'error',
                  value: [msg]
                });
                // this.activeTab = 0;
              } else {
                let errorsArr = [];
                Object.keys(err).forEach(key => {
                  errorsArr.push({
                    field: key,
                    msg: `${this.toUpper(key.replace('_', ' '))} ${err[key][0]}`
                  });
                });

                this.backErrors = errorsArr;
                // Object.keys(err).forEach(key => {
                //   msg += `${key.toUpperCase()}: ${err[key][0]}\n`;
                // });
              }
            }
          });
      } else {
        delete data.accept_policy;
        delete data.accept_terms;

        this.activateUserLvl1({
          activation_code: this.activationCode,
          account: data
        })
          .then(res => {
            this.$router.push('/');
          })
          .catch(e => {
            console.log('user not activated', e, e.response);
            let err = e.response;

            if (err && err.data && err.data.errors) {
              let err = e.response.data.errors;
              let msg = '';

              if (err.base) {
                msg = e.response.data.errors.base;

                this.showGlobalNotification({
                  type: 'error',
                  value: [msg]
                });
                this.activeTab = 0;
              } else {
                let errorsArr = [];
                Object.keys(err).forEach(key => {
                  errorsArr.push({
                    field: key,
                    msg: `${this.toUpper(key.replace('_', ' '))} ${err[key][0]}`
                  });
                });

                this.backErrors = errorsArr;
              }
            }
          })
      }
    },
    addQuery() {
      let queryStr = '';
      
      if (this.pmb.show && this.pmb.number)
        queryStr += `?pmb_id=${this.pmb.number.id}`;

      return queryStr;
    },
    handleCodeSubmit(code) {
      this.codeError = null;
      this.showSpinner = true;
      
      this.validateActivationCode(code)
        .then(res => {
          this.activationCode = code;
          this.formPrefillData = res;
          
          this.view = 'form';
          this.showSpinner = false;

        })
        .catch(e => {
          let err = e.response;

          if (err && err.data && err.data.errors && err.data.errors.base) {
            this.codeError = err.data.errors.base[0];
          }
          this.showSpinner = false;
        });
    },
    handleGenerateVerificationCode(params) {
      this.showSpinner = true;
      this.generateVerificationCode(params)
        .then(res => {
          this.showSpinner = false;
          this.activeTab = 2;
          return res;
        })
        .catch(e => {
          console.log(e);
          this.showSpinner = false;
        });
    },
    hasPaidPlansOnly(plans) {
      return plans.length && !plans.some(plan => 
        plan.subscription_price === 0 || plan.period_amount === 0
      );
    }
  }
}
</script>

<style lang="scss">
  @import '@/assets/scss/general/mixins.scss';
  @import '@/assets/scss/framework/scss-variables.scss';
  @import '@/assets/scss/auth/_reg.scss';
</style>
